export const ABOUT_PERMISSIONS_DIALOG = {
  TITLE: "Contact Management Overview",
  CLOSE_BUTTON: "CLOSE",
} as const;

export const ABOUT_PERMISSIONS_TABS = {
  MANAGING_CONTACTS: "Managing Contacts",
  ROLES: "Roles",
  DOCUMENT_ACCESS: "Document Access",
  NOTIFICATIONS: "Notifications",
  INTERNAL_FUNDS: "Internal Funds",
} as const;

export const MANAGING_CONTACTS_CONTENT = {
  PARAGRAPH_1:
    "In the Manage Contacts table, you can view, filter, add, or remove contacts, as well as update roles, document access, and notification settings for contacts assigned to an investor's fund commitment where you are a Primary or Secondary Contact.",
  PARAGRAPH_2: "Once saved, certain changes may require Carlyle review and will display an In Review status.",
} as const;

export const ROLES_CONTENT = {
  INTRO_PARAGRAPH_1:
    "Roles define a user's authority, as well as their default document access and notification settings.",
  INTRO_PARAGRAPH_2: "Users with Primary or Secondary Contact roles may manage and update other users' permissions.",
  INTRO_PARAGRAPH_3:
    "Each investor's fund commitment must have at least one user assigned to each of the following roles: Primary Contact, Wire Approver, and AML/KYC Contact.",
} as const;

export const ROLE_DEFINITIONS = {
  PRIMARY_CONTACT: {
    title: "PRIMARY CONTACT",
    content: [
      "Primary Contacts manage user access and permissions for the investor's fund commitments they oversee. They can:",
      "• Edit roles, document access, and notification settings for any user assigned to an investor's fund commitments where they are a Primary Contact.",
      "• Add or remove users from an investor's fund commitments.",
      "",
      "**Primary Contacts receive all documents and notifications for assigned investor's fund commitments by default, excluding those related to AML/KYC.**",
    ],
  },
  SECONDARY_CONTACT: {
    title: "SECONDARY CONTACT",
    content: [
      "Secondary Contacts manage user access and permissions for the investor's fund commitments they oversee. They can:",
      "• Edit roles, document access, and notification settings for any user assigned to an investor's fund commitments where they are a Secondary Contact.",
      "• Add or remove users from an investor's fund commitments.",
      "",
      "If a Secondary Contact submits an edit related to a Primary Contact or Wire Approver, a Primary Contact on the investor's fund commitment may be contacted to confirm the change.",
      "",
      "**Secondary Contacts receive all documents and notifications for assigned investor's fund commitments by default, excluding those related to AML/KYC.**",
    ],
  },
  WIRE_APPROVER: {
    title: "WIRE APPROVER",
    content: [
      "Wire Approvers have authority to approve changes to wire instructions.",
      "",
      "Wire Approvers cannot view or change the permissions of other users assigned to the same investor's fund commitments.",
      "",
      "**By default, Wire Approvers access documents and receive notifications related to: Capital Calls and Distributions.**",
    ],
  },
  FINANCE_CONTACT: {
    title: "FINANCE CONTACT",
    content: [
      "Finance Contacts cannot view or change the permissions of other users assigned to the same investor's fund commitments.",
      "",
      "**By default, Finance Contacts access documents and receive notifications related to: Capital Calls, Distributions and Financial Reporting.**",
    ],
  },
  LEGAL_ADVISOR: {
    title: "LEGAL ADVISOR",
    content: [
      "Legal Advisors have authority to send and receive legal communication on behalf of the investor's fund commitments where they are assigned the Legal Advisor role.",
      "",
      "Legal Advisors cannot view or change the permissions of other users assigned to the same investor's fund commitments.",
      "",
      "**By default, Legal Advisors access documents and receive notifications related to: Legal Information.**",
    ],
  },
  TAX_ADVISOR: {
    title: "TAX ADVISOR",
    content: [
      "Tax Advisors have authority to send and receive tax communication on behalf of the investor's fund commitments where they are assigned the Tax Advisor role.",
      "",
      "Tax Advisors cannot view or change the permissions of other users assigned to the same investor's fund commitments.",
      "",
      "**By default, Tax Advisors access documents and receive notifications related to: Tax Information.**",
    ],
  },
  AML_KYC_CONTACT: {
    title: "AML/KYC CONTACT",
    content: [
      "AML/KYC Contacts have authority to manage requests for investor due diligence documentation required under Anti–Money Laundering (AML) and Know Your Customer (KYC) regulations.",
      "",
      "AML/KYC Contacts cannot view or change the permissions of other users assigned to the same fund commitment.",
      "",
      "**By default, AML/KYC Contacts access documents and receive notifications related to: AML/KYC Information.**",
    ],
  },
} as const;

export const DOCUMENT_ACCESS_CONTENT = {
  PARAGRAPH_1:
    "Document access determines which types of documents a user can view in LP Connect. A user's role defines their default document access. This access can be customized by Primary or Secondary Contacts assigned to the same investor's fund commitments.",
  PARAGRAPH_2:
    "When a user is granted document access, they default to receiving email notifications when new documents are available on LP Connect.",
  PARAGRAPH_3:
    "Every investor's fund commitment must include at least one contact with access to: Capital Calls, Distributions, Legal, Tax, and AML/KYC documents.",
} as const;

export const NOTIFICATIONS_CONTENT = {
  PARAGRAPH_1:
    "Notifications control when a user receives email alerts for new documents available in LP Connect, based on the document types they are authorized to access.",
  PARAGRAPH_2:
    "Users can only receive notifications for document types they have access to and may update their notification preferences at any time.",
  PARAGRAPH_3:
    "Every investor's fund commitment is required to have at least one contact with notifications enabled for: Capital Calls, Distributions, Legal, Tax, and AML/KYC information.",
} as const;

export const INTERNAL_FUNDS_CONTENT = {
  INTRO_PARAGRAPH_3:
    "Internal funds do not use document access or notification assignments. Those settings are automatically applied based on each user's role.",
} as const;

export const INTERNAL_FUNDS_ROLE_DEFINITIONS = {
  PRIMARY_CONTACT: {
    title: "PRIMARY CONTACT",
    content: [
      "Primary Contacts manage user access and permissions for internal funds they oversee. They can:",
      "• Edit roles for any user assigned to an investor's internal funds where they are a Primary Contact.",
      "• Add or remove users from internal funds they oversee.",
      "",
      "Only Primary contacts may subscribe to solicitation opportunities and are the first point of contact for communication related to an investor's fund commitment. Only Primary Contacts can authorize updates to wire information.",
      "",
      "**Primary contacts have access to all documents and receive all notifications for internal funds they oversee.**",
    ],
  },
  SECONDARY_CONTACT: {
    title: "SECONDARY CONTACT",
    content: [
      "Secondary Contacts cannot view or change the permissions of other users assigned to the same internal fund.",
      "",
      "Secondary contacts have access to all documents and receive most notifications for internal funds to which they are assigned.",
    ],
  },
  TAX_ADVISOR: {
    title: "TAX ADVISOR",
    content: [
      "Tax advisors cannot view or change the permissions of other users assigned to the same internal fund.",
      "",
      "**Tax Advisors only have access to documents and notifications related to: Tax Information, for the internal funds they are assigned to.**",
    ],
  },
} as const;
